
import React, { useState, useEffect, useRef } from "react";
import { useJournalFlow, JournalStage } from "@/hooks/useJournalFlow";
import { PromptStage } from "@/hooks/dice/useRollerState";
import { DiceRollerRef } from "@/components/DiceRoller";
import JournalEntryView from "@/components/journal/JournalEntryView";
import JournalEditor from "@/components/journal/JournalEditor";
import InteractiveTutorial from "@/components/tutorial/InteractiveTutorial";
import TutorialManager from "@/components/tutorial/TutorialManager";
import { useJournalEntry } from "@/hooks/journal/useJournalEntry";
import { useSettings } from "@/context/SettingsContext";
import { getRpgThemeClasses } from "@/lib/utils";
import { cn } from "@/lib/utils";
import journalPrompts from "@/data/journalPrompts";

const Journal = () => {
  const { darkMode, enableRpgMode, layoutDirection } = useSettings();
  const rpgTheme = getRpgThemeClasses(enableRpgMode, darkMode);
  
  const diceRollerRef = useRef<DiceRollerRef>(null);
  
  const {
    journalEntry,
    isNewEntry,
    activeDiceValue,
    setActiveDiceValue,
    fetchJournalEntry,
    getFormDefaultValues,
    handleSaveEntry,
    entryId,
    setIsNewEntry,
    setJournalEntry
  } = useJournalEntry(journalPrompts);

  const [hasDiceRolled, setHasDiceRolled] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [userHasStartedTyping, setUserHasStartedTyping] = useState(false);
  
  // FIX: Track the last initialized entryId to prevent re-initialization
  const lastInitializedEntryId = useRef<string | undefined>(undefined);

  const { 
    currentStage, 
    advanceStage, 
    resetFlow,
    setStage
  } = useJournalFlow({ 
    initialStage: "stuck",
    onStageChange: (stage) => {
      console.log("Journal: Stage changed to:", stage);
    }
  });

  // FIXED: Handle dice roll WITH automatic stage advancement
  const handleDiceRoll = (value: number) => {
    console.log("===== Journal: handleDiceRoll called =====");
    console.log("Journal: Received dice value:", value, "Type:", typeof value);
    
    if (!value || value < 1 || value > 6) {
      console.error("Journal: Invalid dice value received:", value);
      return;
    }
    
    console.log("Journal: Processing valid dice value:", value);
    console.log("Journal: Current state before update:", {
      activeDiceValue,
      hasDiceRolled,
      currentStage
    });
    
    setActiveDiceValue(value);
    setHasDiceRolled(true);
    
    // FIXED: Automatically advance stage from 'stuck' to 'perception' after dice roll
    if (currentStage === "stuck") {
      console.log("Journal: Automatically advancing stage from 'stuck' to 'perception'");
      advanceStage(); // Advance from 'stuck' to 'perception'
    }
    
    console.log("Journal: Dice roll complete with automatic stage advancement");
    console.log("===== Journal: handleDiceRoll completed =====");
    console.log("Journal: hasDiceRolled is now set to:", true);
    console.log("Journal: activeDiceValue is now set to:", value);
    console.log("Journal: Stage should now be 'perception'");
  };

  // SIMPLIFIED: Only receive stage updates from dice roller, don't send them back
  const handlePromptStageChange = (stage: PromptStage) => {
    console.log("Journal: Received prompt stage change:", stage, "- ignoring to avoid loops");
    // We're not syncing this back to avoid infinite loops
  };

  // Function to trigger dice roll from journal form
  const triggerDiceRoll = () => {
    console.log("Journal: Triggering dice roll from form");
    if (diceRollerRef.current) {
      diceRollerRef.current.triggerRoll();
    }
  };

  // FIX: Function to handle continue button - ONLY way to advance journal stage
  const handleJournalContinue = () => {
    console.log("Journal: Continue button clicked - MANUALLY advancing from:", currentStage);
    
    // FIX: Only advance if we have a valid dice roll
    if (!hasDiceRolled || activeDiceValue <= 0) {
      console.log("Journal: Cannot advance - no valid dice roll yet");
      return;
    }
    
    console.log("Journal: Valid dice roll exists, advancing stage");
    advanceStage();
  };

  // Function to handle user starting to type
  const handleUserStartedTyping = () => {
    console.log("Journal: User started typing - setting protection flag");
    setUserHasStartedTyping(true);
  };

  // Function to handle re-rolling - resets everything to start over
  const handleReRoll = () => {
    console.log("Journal: Re-roll requested - resetting entire journal flow");
    
    // Reset all journal state
    setActiveDiceValue(0);
    setHasDiceRolled(false);
    setUserHasStartedTyping(false);
    
    // Reset flow back to stuck stage
    resetFlow("stuck");
    
    // Trigger a new dice roll
    if (diceRollerRef.current) {
      diceRollerRef.current.triggerRoll();
    }
    
    console.log("Journal: Re-roll complete - ready for new dice roll");
  };

  // FIX: Load existing journal entry with proper dependency array and guards
  useEffect(() => {
    console.log("Journal: useEffect triggered - checking if initialization needed");
    console.log("Journal: entryId:", entryId, "lastInitialized:", lastInitializedEntryId.current, "isInitializing:", isInitializing);
    
    // FIX: Prevent re-initialization if already initializing or if entryId hasn't changed
    if (isInitializing && lastInitializedEntryId.current === entryId) {
      console.log("Journal: Skipping initialization - already initialized for this entryId");
      return;
    }
    
    const initializeJournal = async () => {
      console.log("Journal: Starting initialization for entryId:", entryId);
      setIsInitializing(true);
      
      // FIX: Mark this entryId as being initialized
      lastInitializedEntryId.current = entryId;
      
      try {
        if (entryId) {
          const entry = await fetchJournalEntry(entryId);
          if (entry) {
            console.log("Journal: Loaded existing entry:", entry);
            
            let initialStage: JournalStage = "stuck";
            
            if (entry.next_step_response) {
              initialStage = "completed";
            } else if (entry.intention_response) {
              initialStage = "nextStep";
            } else if (entry.perception_response) {
              initialStage = "intention";
            } else if (entry.stuck_point) {
              initialStage = "perception";
            }
            
            console.log("Journal: Calling resetFlow for existing entry with stage:", initialStage);
            resetFlow(initialStage);
            
            if (entry.dice_value) {
              setActiveDiceValue(entry.dice_value);
              setHasDiceRolled(true);
            }
            
            // FIX: If entry has content, user has already started typing
            if (entry.stuck_point) {
              setUserHasStartedTyping(true);
            }
          }
        } else {
          console.log("Journal: Creating new entry");
          setIsNewEntry(true);
          console.log("Journal: Calling resetFlow for new entry");
          resetFlow("stuck");
          setActiveDiceValue(0);
          setHasDiceRolled(false);
          setUserHasStartedTyping(false);
        }
      } catch (error) {
        console.error("Journal: Error initializing:", error);
      } finally {
        setTimeout(() => {
          console.log("Journal: Initialization complete");
          setIsInitializing(false);
        }, 500);
      }
    };
    
    initializeJournal();
  }, [entryId]); // FIX: Only depend on entryId to prevent unnecessary re-runs

  console.log("Journal render:", { 
    currentStage, 
    activeDiceValue, 
    hasDiceRolled, 
    isInitializing,
    userHasStartedTyping,
    layoutDirection
  });

  // Define the order of components based on layout direction
  const journalComponent = (
    <div className="flex flex-col">
      <JournalEditor
        journalEntry={journalEntry}
        isNewEntry={isNewEntry}
        formDefaultValues={getFormDefaultValues()}
        currentStage={currentStage}
        activeDiceValue={activeDiceValue}
        journalPrompts={journalPrompts}
        onContinue={handleJournalContinue}
        onRollDice={triggerDiceRoll}
        onSave={handleSaveEntry}
        hasDiceRolled={hasDiceRolled}
        userHasStartedTyping={userHasStartedTyping}
        onUserStartedTyping={handleUserStartedTyping}
        onReRoll={handleReRoll}
      />
    </div>
  );

  const diceComponent = (
    <div className="flex flex-col">
      <JournalEntryView 
        diceRollerRef={diceRollerRef}
        onDiceRoll={handleDiceRoll}
        onPromptStageChange={handlePromptStageChange}
        hasDiceRolled={hasDiceRolled}
      />
    </div>
  );

  return (
    <TutorialManager>
      <div className={cn(
        "container py-8",
        enableRpgMode && rpgTheme.background
      )}>
        {/* Interactive Tutorial */}
        <InteractiveTutorial />
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {layoutDirection === 'ltr' ? (
            <>
              {journalComponent}
              {diceComponent}
            </>
          ) : (
            <>
              {diceComponent}
              {journalComponent}
            </>
          )}
        </div>
      </div>
    </TutorialManager>
  );
};

export default Journal;
